import React, { useRef, useState } from 'react';
import { useAuth } from '../helpers/AuthContext';

function MediaUploader({ accept = '', onUploadSuccess }) {
    const { fetch }  = useAuth();
    const fileInputRef = useRef();
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [uploaded, setUploaded] = useState(false);

    const reset = () => {
        setUploading(false);
        setProgress(0);
        setError('');
        setUploaded(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleFileChange = async (e) => {
        setError('');
        setUploaded(false);
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        setProgress(0);
        try {
            // Step 1: Get upload URL
            const urlRes = await fetch(`/account/media/upload-get-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filename: file.name,
                    size: file.size,
                    mimetype: file.type,
                })
            });
            if (!urlRes.ok) {
                const data = await urlRes.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to get upload URL');
            }
            const uploadUrlData = await urlRes.json();

            // Step 2: Upload file to storage
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', uploadUrlData.uploadUrl, true);
                xhr.setRequestHeader('Content-Type', file.type);
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        setProgress((event.loaded / event.total) * 100);
                    }
                });
                xhr.onload = () => {
                    if (xhr.status === 200) {
                        resolve();
                    } else {
                        let msg = 'Failed to upload file';
                        try {
                            const resp = JSON.parse(xhr.responseText);
                            if (resp && resp.message) msg = resp.message;
                        } catch {}
                        reject(new Error(msg));
                    }
                };
                xhr.onerror = () => reject(new Error('Upload failed. Please try again.'));
                xhr.onabort = () => reject(new Error('Upload aborted.'));
                xhr.ontimeout = () => reject(new Error('Upload timed out.'));
                xhr.send(file);
            });

            // Step 3: Finalize upload
            const finalizeRes = await fetch(`/account/media/upload-finalize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(uploadUrlData)
            });
            if (!finalizeRes.ok) {
                const data = await finalizeRes.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to finalize upload');
            }
            const finalizeData = await finalizeRes.json();
            setUploaded(true);
            setUploading(false);
            setProgress(100);
            if (onUploadSuccess && finalizeData.file && finalizeData.file.id) {
                onUploadSuccess(finalizeData.file.id, finalizeData.file);
            }
        } catch (err) {
            setError(err.message || 'An error occurred during the upload process.');
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <div className="upload" style={{ display: 'block', paddingTop: '1rem' }}>
            <div className="group">
                <label htmlFor="file">Select File</label>
                <input
                    ref={fileInputRef}
                    type="file"
                    className="media-picker-upload-file"
                    accept={accept}
                    onChange={handleFileChange}
                    disabled={uploading}
                />
                <p className="help after-upload" style={{ display: uploaded ? 'block' : 'none' }}>
                    File uploaded. Make sure to click Save below.
                </p>
            </div>
            <div className="group" style={{ display: uploading ? 'block' : 'none' }}>
                <label>Uploading, Please Wait...</label>
                <progress className="media-picker-upload-progress" value={progress} max={100}></progress>
                <p className="help">Please wait until the upload completes.</p>
            </div>
            <div className="group" style={{ display: error ? 'block' : 'none' }}>
                <label>Error Uploading File</label>
                <p className="error media-picker-upload-error">{error}</p>
            </div>
        </div>
    );
}

export default MediaUploader;

